import { useEffect, useRef, useState } from "react";
import { Minus, Plus } from "lucide-react";
import type { PDFDocumentProxy } from "pdfjs-dist";
import type { AnnotationView } from "@/features/meeting/services/annotation-geometry";
import { loadPdfViewer } from "./pdf-loader";

/** Zoom is a multiple of the viewer's width; 1 fits a page to it. */
const MAX_ZOOM = 3;
/** A phone screen is 3x; painting at that, page by page, runs a phone out of memory. */
const MAX_PIXEL_RATIO = 2;

/** "page" fits the whole first page in view; a number is a multiple of the width. */
type Zoom = "page" | number;

/** How often the presenter's position goes out while they scroll. */
const VIEW_REPORT_MS = 150;

/**
 * A shared PDF painted with pdf.js: every page, one under the other, scrolling.
 * Pages are painted as they come near the viewport, so a long deck does not
 * paint (and hold) every page up front.
 *
 * A mouse-driven screen opens on the whole page: fitted to the width, a
 * portrait page on a desktop is three screens tall and the room sees a third
 * of it. A touch screen opens fitted to the width, which is what reads on a
 * phone, and pinches or zooms from there.
 */
export function SharedPdfViewer({
  url,
  name,
  renderPageOverlay,
  onViewChange,
  followView,
  onUserScroll,
}: {
  url: string;
  name: string;
  /** Drawn over each page, inside a box the page's exact size — the annotation
   *  layer. Gets the 1-based page number and the page's height over width. */
  renderPageOverlay?: (page: number, ratio: number) => React.ReactNode;
  /** The presenter's side: where the top of the view is, as they scroll. */
  onViewChange?: (view: AnnotationView) => void;
  /** A viewer's side: the presenter's position, to keep the view on. Null stops following. */
  followView?: AnnotationView | null;
  /** The viewer scrolled by hand — wheel, touch, keys or the scrollbar. */
  onUserScroll?: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  // Height over width of the first page, which decides what "whole page" is.
  const [firstRatio, setFirstRatio] = useState(1.414);
  const [zoom, setZoom] = useState<Zoom>(() =>
    typeof window !== "undefined" && window.matchMedia?.("(pointer: fine)").matches ? "page" : 1,
  );
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    let doc: PDFDocumentProxy | null = null;
    setPdf(null);
    setError(null);
    (async () => {
      // Usually already loaded: see pdf-loader.ts.
      const { pdfjs, worker } = await loadPdfViewer();
      doc = await pdfjs.getDocument({ url, ...(worker && !worker.destroyed ? { worker } : {}) }).promise;
      const first = (await doc.getPage(1)).getViewport({ scale: 1 });
      if (cancelled) {
        void doc.destroy();
        return;
      }
      setFirstRatio(first.height / first.width);
      setPdf(doc);
    })().catch((err: unknown) => {
      if (cancelled) return;
      console.warn("[share] could not open the shared PDF:", err);
      setError("This PDF could not be opened.");
    });
    return () => {
      cancelled = true;
      if (doc) void doc.destroy();
    };
  }, [url]);

  // Pages are fitted to the viewer's width, so they follow it — a phone turned
  // on its side, or the participant strip appearing.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => {
      setWidth(el.clientWidth);
      setHeight(el.clientHeight);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const pageCount = pdf?.numPages ?? 0;

  // Which page is on screen, for the counter; and, for a presenter, where the
  // top of their view is, so viewers can be kept on it.
  const viewTimer = useRef<number | null>(null);
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el || !pageCount) return;
    const mid = el.scrollTop + el.clientHeight / 2;
    const pages = el.querySelectorAll<HTMLElement>("[data-pdf-page]");
    for (const p of pages) {
      if (p.offsetTop + p.offsetHeight >= mid) {
        setPage(Number(p.dataset.pdfPage));
        break;
      }
    }
    if (onViewChange && viewTimer.current === null) {
      viewTimer.current = window.setTimeout(() => {
        viewTimer.current = null;
        const view = viewAt(scrollRef.current);
        if (view) onViewChange(view);
      }, VIEW_REPORT_MS);
    }
  };
  useEffect(() => () => {
    if (viewTimer.current !== null) window.clearTimeout(viewTimer.current);
  }, []);

  // The 16px is the scroller's padding, on each axis.
  const fitWidth = Math.max(0, width - 16);
  const fitPage = fitWidth && height > 16 ? Math.min(1, (height - 16) / (fitWidth * firstRatio)) : 1;
  const scale = zoom === "page" ? fitPage : zoom;
  const pageWidth = fitWidth * scale;

  // In from the whole page goes to the full width first, then in halves; out
  // retraces that and stops at the whole page.
  const zoomIn = () => setZoom(scale < 1 ? 1 : Math.min(MAX_ZOOM, scale + 0.5));
  const zoomOut = () => setZoom(scale <= 1 ? "page" : Math.max(1, scale - 0.5));

  // Following: put the presenter's position at the top of this view. Relative
  // to the page, so it lands right whatever this viewer's zoom — and again
  // after a zoom, which moves every page.
  useEffect(() => {
    const el = scrollRef.current;
    if (!followView || !el || !pdf || !pageWidth) return;
    const p = el.querySelector<HTMLElement>(`[data-pdf-page="${followView.page}"]`);
    if (p) el.scrollTop = p.offsetTop + followView.offset * p.offsetHeight;
  }, [followView, pdf, pageWidth]);

  // A hand on the document. Told apart from the scrolling done above by what
  // causes it, not by the scroll itself: following and zooming scroll too.
  const userScroll = onUserScroll
    ? {
        onWheel: onUserScroll,
        onTouchMove: onUserScroll,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes(e.key)) onUserScroll();
        },
        // Only on the scroller itself, which is its scrollbar — not a pen
        // stroke on a page.
        onPointerDown: (e: React.PointerEvent) => {
          if (e.target === e.currentTarget) onUserScroll();
        },
      }
    : {};

  return (
    <div className="absolute inset-0 flex flex-col bg-slate-200">
      <div
        ref={scrollRef}
        onScroll={onScroll}
        {...userScroll}
        tabIndex={onUserScroll ? 0 : undefined}
        data-testid="shared-pdf-viewer"
        // `relative` makes this the pages' offsetParent, so a page's offsetTop
        // is its position in the scrolled content.
        className="relative min-h-0 flex-1 overflow-auto overscroll-contain px-2 py-2 outline-none"
      >
        {error ? (
          <p className="p-4 text-center text-sm text-slate-700">{error}</p>
        ) : !pdf || !pageWidth ? (
          <div className="flex h-full items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
          </div>
        ) : (
          <div className="mx-auto flex flex-col items-start gap-2" style={{ width: pageWidth }}>
            {Array.from({ length: pageCount }, (_, i) => (
              <PdfPage
                key={i}
                pdf={pdf}
                pageNumber={i + 1}
                width={pageWidth}
                root={scrollRef}
                name={name}
                renderOverlay={renderPageOverlay}
              />
            ))}
          </div>
        )}
      </div>
      {pdf && (
        <div className="pointer-events-none absolute bottom-2 left-0 right-0 flex items-center justify-center">
          <div className="pointer-events-auto flex items-center gap-1 rounded-full bg-black/70 px-2 py-1 text-xs text-white">
            <button
              type="button"
              aria-label="Zoom out"
              disabled={scale <= fitPage}
              onClick={zoomOut}
              className="flex h-7 w-7 items-center justify-center rounded-full disabled:opacity-40"
            >
              <Minus className="h-4 w-4" />
            </button>
            <span className="min-w-[3.5rem] text-center tabular-nums">
              {page} / {pageCount}
            </span>
            <button
              type="button"
              aria-label="Zoom in"
              disabled={scale >= MAX_ZOOM}
              onClick={zoomIn}
              className="flex h-7 w-7 items-center justify-center rounded-full disabled:opacity-40"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PdfPage({
  pdf,
  pageNumber,
  width,
  root,
  name,
  renderOverlay,
}: {
  pdf: PDFDocumentProxy;
  pageNumber: number;
  width: number;
  root: React.RefObject<HTMLDivElement | null>;
  name: string;
  renderOverlay?: (page: number, ratio: number) => React.ReactNode;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Height over width, known once the page is read. A4 until then, so the
  // placeholders already scroll like the document will.
  const [ratio, setRatio] = useState(1.414);
  const [near, setNear] = useState(false);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setNear(true);
      },
      { root: root.current, rootMargin: "100% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [root]);

  useEffect(() => {
    if (!near || !width) return;
    let cancelled = false;
    let task: { cancel: () => void } | null = null;
    (async () => {
      const page = await pdf.getPage(pageNumber);
      if (cancelled) return;
      const base = page.getViewport({ scale: 1 });
      setRatio(base.height / base.width);
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
      const viewport = page.getViewport({ scale: (width / base.width) * dpr });
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!canvas || !ctx) return;
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const render = page.render({ canvasContext: ctx, viewport });
      task = render;
      await render.promise;
    })().catch((err: unknown) => {
      // A re-render (zoom, rotation) cancels the one in flight; that is not a failure.
      if ((err as { name?: string })?.name !== "RenderingCancelledException") {
        console.warn(`[share] could not paint page ${pageNumber}:`, err);
      }
    });
    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [near, pdf, pageNumber, width]);

  return (
    <div
      ref={boxRef}
      data-pdf-page={pageNumber}
      className="relative shrink-0 bg-white shadow"
      style={{ width, height: width * ratio }}
    >
      <canvas
        ref={canvasRef}
        aria-label={`${name}, page ${pageNumber}`}
        className="block h-full w-full"
      />
      {renderOverlay?.(pageNumber, ratio)}
    </div>
  );
}

/** The page at the top of a scroller's view, and how far down it the view starts. */
function viewAt(el: HTMLElement | null): AnnotationView | null {
  if (!el) return null;
  const top = el.scrollTop;
  for (const p of el.querySelectorAll<HTMLElement>("[data-pdf-page]")) {
    if (p.offsetTop + p.offsetHeight > top) {
      const offset = Math.min(1, Math.max(0, (top - p.offsetTop) / p.offsetHeight));
      return { page: Number(p.dataset.pdfPage), offset: Math.round(offset * 1000) / 1000 };
    }
  }
  return null;
}
